import { type ExprNode } from '@learnforge/demo-math/expr';
import { formalDerivative } from './derivative';
import { evaluate } from './evaluate';

const DEFAULT_TOL = 1e-9;
const DEFAULT_STEPS = 200;

export function findZerosBisection(
  node: ExprNode,
  a: number,
  b: number,
  tol: number = DEFAULT_TOL,
): number[] {
  const out: number[] = [];
  if (b <= a) return out;
  const samples = Math.max(2, Math.ceil((b - a) / 0.05));
  let prevX = a;
  let prevY = evaluate(node, prevX);
  for (let i = 1; i <= samples; i++) {
    const x = a + ((b - a) * i) / samples;
    const y = evaluate(node, x);
    if (!Number.isNaN(prevY) && !Number.isNaN(y) && prevY === 0) {
      if (out[out.length - 1] !== prevX) out.push(prevX);
    }
    if (
      !Number.isNaN(prevY) &&
      !Number.isNaN(y) &&
      Math.sign(prevY) !== Math.sign(y) &&
      prevY !== 0 &&
      y !== 0
    ) {
      let lo = prevX;
      let hi = x;
      let loY = prevY;
      for (let j = 0; j < 80 && hi - lo > tol; j++) {
        const mid = (lo + hi) / 2;
        const midY = evaluate(node, mid);
        if (Number.isNaN(midY)) break;
        if (Math.sign(midY) === Math.sign(loY)) {
          lo = mid;
          loY = midY;
        } else {
          hi = mid;
        }
      }
      out.push((lo + hi) / 2);
    }
    prevX = x;
    prevY = y;
  }
  return out.sort((p, q) => p - q);
}

export function findZeroNewton(
  node: ExprNode,
  seed: number,
  tol: number = DEFAULT_TOL,
): number | null {
  const derivNode = formalDerivative(node);
  let x = seed;
  for (let i = 0; i < DEFAULT_STEPS; i++) {
    const y = evaluate(node, x);
    if (!Number.isFinite(y)) return null;
    if (Math.abs(y) < tol) return x;
    const dy = evaluate(derivNode, x);
    if (!Number.isFinite(dy) || Math.abs(dy) < 1e-14) return null;
    const next = x - y / dy;
    if (!Number.isFinite(next)) return null;
    if (Math.abs(next - x) < tol && Math.abs(y) < 1e-6) return next;
    x = next;
    if (Math.abs(x) > 1e12) return null;
  }
  return null;
}
